import { useEffect, useRef, useMemo, useState } from 'react'
import { Search, X, Clock, Library, ChevronDown, Tag, FolderOpen } from 'lucide-react'
import { useContentCpStore, type CardIngestionSummary, type SearchMode, type SpaceFilter } from '@/stores/useContentCpStore'
import { fetchSessionCards, fetchSessionCard } from '@/lib/api'
import type { ContentCardSummary } from '@/lib/api'
import { cn } from '@/lib/utils'
import { ContentCpCardDetail } from './ContentCpCardDetail'

const SEARCH_MODES: { value: SearchMode; label: string }[] = [
  { value: 'semantic', label: 'Semantic' },
  { value: 'keyword', label: 'Keyword' },
  { value: 'wildcard', label: 'Wildcard' },
]

// Only 4 flywheel spaces (Entity is the container, not a space)
const SPACES: SpaceFilter[] = ['Assets', 'Operations', 'Develop', 'Identity']

export function ContentCpCardsPanel() {
  const {
    sessionId,
    lastIngestionResult,
    selectedCardId,
    selectedSourceId,
    setSelectedCardId,
    navigateBack,
    libraryCards,
    setLibraryCards,
    isLoadingLibrary,
    setIsLoadingLibrary,
    selectedCardsTab,
    setSelectedCardsTab,
    recentCardIds,
    getSessionCards,
    // Search state
    searchQuery,
    setSearchQuery,
    searchMode,
    setSearchMode,
    filterTags,
    filterSpaces,
    toggleFilterSpace,
    clearFilters,
  } = useContentCpStore()

  // Get session card IDs (persistent per session)
  const sessionCardIds = getSessionCards()

  // State for recent cards details (latest ingestion only)
  const [recentCards, setRecentCards] = useState<ContentCardSummary[]>([])
  const [isLoadingRecent, setIsLoadingRecent] = useState(false)

  // State for session cards details (all cards in session)
  const [sessionCards, setSessionCards] = useState<ContentCardSummary[]>([])
  const [isLoadingSession, setIsLoadingSession] = useState(false)

  // Track newly ingested card IDs for highlighting
  const newCardIds = useMemo(() => {
    if (!lastIngestionResult?.cards) return new Set<string>()
    return new Set(lastIngestionResult.cards.map((c) => c.card_id))
  }, [lastIngestionResult])

  // Track if we've fetched for the current search/filters
  const lastFetchedQuery = useRef<string | null>(null)
  const lastFetchedIngestion = useRef<CardIngestionSummary | null>(null)
  const lastFetchedMode = useRef<SearchMode | null>(null)
  const lastFetchedSpaces = useRef<string | null>(null)
  const lastFetchedTags = useRef<string | null>(null)

  // Fetch recent cards when recentCardIds changes (latest ingestion only)
  useEffect(() => {
    if (!sessionId || recentCardIds.length === 0) {
      setRecentCards([])
      return
    }

    setIsLoadingRecent(true)
    Promise.all(
      recentCardIds.slice(0, 50).map((id) => fetchSessionCard(sessionId, id).catch(() => null))
    )
      .then((cards) => {
        // Convert ContentCardDetail to ContentCardSummary format
        setRecentCards(
          cards
            .filter((c) => c !== null)
            .map((c) => ({
              card_id: c!.card_id,
              type: c!.type,
              domain: c!.domain,
              title: c!.title,
              summary: c!.summary,
              tags: c!.tags,
              support_count: c!.support_count,
              created_at: c!.created_at,
            }))
        )
      })
      .catch(console.error)
      .finally(() => setIsLoadingRecent(false))
  }, [sessionId, recentCardIds])

  // Fetch session cards when sessionCardIds changes (all cards from session)
  useEffect(() => {
    if (!sessionId || sessionCardIds.length === 0) {
      setSessionCards([])
      return
    }

    setIsLoadingSession(true)
    Promise.all(
      sessionCardIds.slice(0, 100).map((id) => fetchSessionCard(sessionId, id).catch(() => null))
    )
      .then((cards) => {
        // Convert ContentCardDetail to ContentCardSummary format
        setSessionCards(
          cards
            .filter((c) => c !== null)
            .map((c) => ({
              card_id: c!.card_id,
              type: c!.type,
              domain: c!.domain,
              title: c!.title,
              summary: c!.summary,
              tags: c!.tags,
              support_count: c!.support_count,
              created_at: c!.created_at,
            }))
        )
      })
      .catch(console.error)
      .finally(() => setIsLoadingSession(false))
  }, [sessionId, sessionCardIds])

  // Fetch library cards (all cards in session) on mount, search changes, or new card ingested
  useEffect(() => {
    if (selectedCardsTab !== 'library' || !sessionId) return

    const doFetch = () => {
      setIsLoadingLibrary(true)
      fetchSessionCards(sessionId, {
        search: searchQuery || undefined,
        domain: filterSpaces.length === 1 ? filterSpaces[0] : undefined,
        page_size: 50,
      })
        .then((result) => {
          // Client-side filtering for multiple spaces
          let filtered = result.cards

          if (filterSpaces.length > 1) {
            filtered = filtered.filter((c) =>
              filterSpaces.some((s) => s.toLowerCase() === c.domain?.toLowerCase())
            )
          }

          setLibraryCards(filtered)
          lastFetchedQuery.current = searchQuery
          lastFetchedIngestion.current = lastIngestionResult
          lastFetchedMode.current = searchMode
          lastFetchedSpaces.current = filterSpaces.join(',')
          lastFetchedTags.current = filterTags.join(',')
        })
        .catch(console.error)
        .finally(() => setIsLoadingLibrary(false))
    }

    // Check if we need to refetch
    const queryChanged = lastFetchedQuery.current !== searchQuery
    const ingestionChanged =
      lastIngestionResult !== null && lastFetchedIngestion.current !== lastIngestionResult
    const modeChanged = lastFetchedMode.current !== searchMode
    const spacesChanged = lastFetchedSpaces.current !== filterSpaces.join(',')
    const tagsChanged = lastFetchedTags.current !== filterTags.join(',')

    if (queryChanged || ingestionChanged || modeChanged || spacesChanged || tagsChanged) {
      if (lastFetchedQuery.current === null && !ingestionChanged) {
        // First fetch - immediate
        doFetch()
      } else if (ingestionChanged) {
        // New card ingested - fetch immediately
        doFetch()
      } else {
        // Changes - debounce
        const timer = setTimeout(doFetch, 300)
        return () => clearTimeout(timer)
      }
    }
  }, [
    sessionId,
    selectedCardsTab,
    searchQuery,
    searchMode,
    filterSpaces,
    filterTags,
    setIsLoadingLibrary,
    setLibraryCards,
    lastIngestionResult,
  ])

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Tab bar */}
      <div className="flex-shrink-0 flex items-center gap-1 px-3 py-2 border-b border-border">
        <button
          onClick={() => setSelectedCardsTab('recent')}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded text-sm transition-colors',
            selectedCardsTab === 'recent'
              ? 'bg-muted/50 text-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
          )}
        >
          <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span className="font-light">Recent</span>
          {recentCardIds.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-muted rounded-full text-xs">
              {recentCardIds.length}
            </span>
          )}
        </button>

        <button
          onClick={() => setSelectedCardsTab('session')}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded text-sm transition-colors',
            selectedCardsTab === 'session'
              ? 'bg-muted/50 text-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
          )}
        >
          <FolderOpen className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span className="font-light">Session</span>
          {sessionCards.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-muted rounded-full text-xs">
              {sessionCards.length}
            </span>
          )}
        </button>

        <button
          onClick={() => setSelectedCardsTab('library')}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded text-sm transition-colors',
            selectedCardsTab === 'library'
              ? 'bg-muted/50 text-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
          )}
        >
          <Library className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span className="font-light">Library</span>
        </button>
      </div>

      {/* Card or source detail view */}
      {selectedCardId || selectedSourceId ? (
        <ContentCpCardDetail
          cardId={selectedCardId}
          sourceId={selectedSourceId}
          onBack={navigateBack}
        />
      ) : selectedCardsTab === 'recent' ? (
        /* Recent tab content - latest ingestion only */
        <div className="flex-1 overflow-y-auto">
          {isLoadingRecent ? (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">Loading...</p>
            </div>
          ) : recentCards.length === 0 ? (
            <div className="h-full flex items-center justify-center px-6 text-center">
              <div className="text-muted-foreground text-sm max-w-sm space-y-2">
                <p className="font-medium text-foreground">Latest Extraction</p>
                <p>Cards from your most recent extraction will appear here.</p>
                <p className="text-xs">Highlighted in green when new.</p>
              </div>
            </div>
          ) : (
            <CardTable
              cards={recentCards.map((c) => ({
                card_id: c.card_id,
                title: c.title || 'Untitled',
                domain: c.domain || '',
                type: c.type || '',
                isNew: newCardIds.has(c.card_id),
              }))}
              onCardClick={setSelectedCardId}
            />
          )}
        </div>
      ) : selectedCardsTab === 'session' ? (
        /* Session tab content - all cards from this session */
        <div className="flex-1 overflow-y-auto">
          {isLoadingSession ? (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">Loading...</p>
            </div>
          ) : sessionCards.length === 0 ? (
            <div className="h-full flex items-center justify-center px-6 text-center">
              <div className="text-muted-foreground text-sm max-w-sm space-y-2">
                <p className="font-medium text-foreground">Session Cards</p>
                <p>All cards extracted in this session will appear here.</p>
                <p className="text-xs">Persists across page refreshes.</p>
              </div>
            </div>
          ) : (
            <CardTable
              cards={sessionCards.map((c) => ({
                card_id: c.card_id,
                title: c.title || 'Untitled',
                domain: c.domain || '',
                type: c.type || '',
                isNew: newCardIds.has(c.card_id),
              }))}
              onCardClick={setSelectedCardId}
            />
          )}
        </div>
      ) : (
        /* Library tab content */
        <>
          {/* Search section */}
          <div className="flex-shrink-0 px-3 py-3 border-b border-border space-y-2">
            {/* Search input with mode dropdown */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={
                    searchMode === 'semantic'
                      ? 'Semantic search...'
                      : searchMode === 'keyword'
                        ? 'Keyword search...'
                        : 'Wildcard search (*, ?)...'
                  }
                  className="w-full pl-9 pr-8 py-2 bg-muted/50 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                )}
              </div>

              {/* Search mode dropdown */}
              <div className="relative">
                <select
                  value={searchMode}
                  onChange={(e) => setSearchMode(e.target.value as SearchMode)}
                  className="appearance-none px-3 pr-7 py-2 bg-muted/50 border border-border rounded-lg text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer"
                >
                  {SEARCH_MODES.map((mode) => (
                    <option key={mode.value} value={mode.value}>
                      {mode.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
              </div>
            </div>

            {/* Filter chips */}
            <div className="flex flex-wrap gap-1.5">
              {/* Space filters */}
              {SPACES.map((space) => (
                <button
                  key={space}
                  onClick={() => toggleFilterSpace(space)}
                  className={cn(
                    'px-2 py-0.5 text-xs rounded-full transition-colors',
                    filterSpaces.includes(space)
                      ? 'bg-foreground text-background'
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  {space}
                </button>
              ))}

              {/* Tag filter button (placeholder for tag selector) */}
              {filterTags.length > 0 && (
                <button
                  onClick={() => clearFilters()}
                  className="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400"
                >
                  <Tag className="w-3 h-3" />
                  {filterTags.length} tags
                  <X className="w-3 h-3 ml-0.5" />
                </button>
              )}

              {/* Clear all filters */}
              {(filterSpaces.length > 0 || filterTags.length > 0) && (
                <button
                  onClick={clearFilters}
                  className="px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Cards list */}
          <div className="flex-1 overflow-y-auto">
            {isLoadingLibrary ? (
              <div className="text-center py-8">
                <p className="text-sm text-muted-foreground">Loading...</p>
              </div>
            ) : libraryCards.length === 0 ? (
              <div className="text-center py-8 px-4">
                <p className="text-sm text-muted-foreground">
                  {searchQuery || filterSpaces.length > 0 || filterTags.length > 0
                    ? 'No cards match your search'
                    : 'No cards in library'}
                </p>
              </div>
            ) : (
              <CardTable
                cards={libraryCards.map((c) => ({
                  card_id: c.card_id,
                  title: c.title || 'Untitled',
                  domain: c.domain || '',
                  type: c.type,
                  isNew: newCardIds.has(c.card_id),
                }))}
                onCardClick={setSelectedCardId}
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}

// Compact table view for cards
interface CardTableProps {
  cards: {
    card_id: string
    title: string
    domain: string
    type: string
    isNew?: boolean
  }[]
  onCardClick: (cardId: string | null) => void
}

function CardTable({ cards, onCardClick }: CardTableProps) {
  return (
    <table className="w-full text-xs">
      <thead className="bg-background sticky top-0 border-b border-border">
        <tr>
          <th className="px-3 py-2 text-left font-medium">Title</th>
          <th className="px-3 py-2 text-left font-medium w-24">Space</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {cards.map((card) => (
          <tr
            key={card.card_id}
            onClick={() => onCardClick(card.card_id)}
            className={cn(
              'cursor-pointer transition-colors',
              card.isNew
                ? 'bg-green-500/10 hover:bg-green-500/20 animate-pulse-once'
                : 'hover:bg-muted/30'
            )}
          >
            <td className="px-3 py-2">
              <span
                className={cn(
                  'font-medium line-clamp-1',
                  card.isNew ? 'text-green-600 dark:text-green-400' : 'text-foreground'
                )}
              >
                {card.title}
              </span>
            </td>
            <td
              className={cn(
                'px-3 py-2 capitalize',
                card.isNew
                  ? 'text-green-600/70 dark:text-green-400/70'
                  : 'text-muted-foreground'
              )}
            >
              {card.domain?.toLowerCase() || '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
